import type { UIStateAPI } from "@/types";
import { zHelpers } from "@/data/schemas";

interface HelpersAdapterDeps {
  readonly store: any;
  readonly uiState: UIStateAPI;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
}

export const hydrateHelpersFromSession = (
  deps: Pick<HelpersAdapterDeps, "store" | "getPath">,
  screen: Record<string, unknown> | null | undefined
): void => {
  if (!screen) return;
  const { store, getPath } = deps;
  const hPath = getPath("helpers");
  if (!hPath) return;
  try {
    store.registerSchema(hPath, zHelpers);
  } catch {}
  try {
    const helpersObj = (screen as any).helpers;
    if (helpersObj && typeof helpersObj === "object") {
      store.set(hPath, helpersObj as any, { source: "hydration" });
    }
  } catch {}
};

export const registerHelpersAdapter = (
  deps: HelpersAdapterDeps
): (() => void) => {
  const { store, uiState, eventBus, getPath, persist } = deps;

  const unsubscribes: (() => void)[] = [];

  // Debug toggles
  const readDebugEnabled = (): boolean => {
    try {
      const d =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof d !== "string") return !!(d as any)?.helpers || d === "*";
      const parts = d.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("helpers");
    } catch {
      return false;
    }
  };
  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (debugEnabled) {
      try {
        // eslint-disable-next-line no-console
        console.debug("[adapter:helpers]", ...args);
      } catch {}
    }
  };
  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        try {
          if (!payload) return;
          if (payload.name === "helpers") debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {}
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {}

  // Store -> UIState for helpers
  const unsubscribeStoreHelpers = store.subscribe(
    (path: string) => path.endsWith(".screenState.helpers"),
    ({ value, source }: { value: unknown; source?: string }) => {
      const isHydration = source === "hydration";
      try {
        const flags = (value as any) || {};
        const nextHelpers: string[] = [];
        if (flags.rulers) nextHelpers.push("rulers");
        if (flags.snap) nextHelpers.push("snap");
        if (flags.guides) nextHelpers.push("guides");
        if (flags.dots) nextHelpers.push("dots");
        if (flags.lines) nextHelpers.push("lines");
        if (flags.overlay) nextHelpers.push("overlay");
        (uiState as any).setHelpers(nextHelpers);
        debug("store→UI helpers", { value, source });
      } catch {}
      if (!isHydration && source !== "socket") persist();
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreHelpers);

  // UIState -> Store for helpers (via eventBus)
  const unsubscribeUIStateChanged = eventBus.on(
    "ui-state-changed",
    (data: any) => {
      try {
        const helpersArr = (data?.state?.helpers as any[]) || [];
        const hPath = getPath("helpers");
        if (!hPath) return;
        const flags = {
          rulers: helpersArr.includes("rulers" as any),
          snap: helpersArr.includes("snap" as any),
          guides: helpersArr.includes("guides" as any),
          dots: helpersArr.includes("dots" as any),
          lines: helpersArr.includes("lines" as any),
          overlay: helpersArr.includes("overlay" as any),
        };
        try {
          store.registerSchema(hPath, zHelpers);
        } catch {}
        debug("UI→store helpers", flags);
        store.set(hPath, flags as any, { source: "helpersAdapter" });
      } catch {}
    }
  );
  unsubscribes.push(unsubscribeUIStateChanged);

  const cleanup = () => {
    for (const u of unsubscribes) {
      try {
        u();
      } catch {}
    }
  };

  try {
    const unsubscribeAppDestroy = eventBus.on("app:destroy", () => cleanup());
    unsubscribes.push(unsubscribeAppDestroy);
  } catch {}

  return cleanup;
};
